/**
 * Per-assignment R&D share bounds (percent at the UI/import edge; fractions on the wire/DB).
 *
 * Product defaults live in `app_settings.rd_share_limits` (1%…95%). The same max also caps
 * the sum of active assignment shares for one person (`RD_SHARE_EXCEEDS_MAX`).
 */

export const RD_SHARE_LIMITS_DEFAULT = {
  minPercent: 1,
  maxPercent: 95,
} as const;

export interface RdShareLimits {
  minPercent: number;
  maxPercent: number;
}

export function normalizeRdShareLimits(raw: {
  min_percent: number;
  max_percent: number;
}): RdShareLimits {
  return {
    minPercent: raw.min_percent,
    maxPercent: raw.max_percent,
  };
}

const SHARE_EPS = 1e-9;

/** Fraction equivalents of the configured percent bounds. */
export function rdShareFractionBounds(limits: RdShareLimits): {
  minShare: number;
  maxShare: number;
} {
  return {
    minShare: limits.minPercent / 100,
    maxShare: limits.maxPercent / 100,
  };
}

export function isRdSharePercentInRange(percent: number, limits: RdShareLimits): boolean {
  if (!Number.isFinite(percent)) return false;
  return percent + SHARE_EPS >= limits.minPercent && percent - SHARE_EPS <= limits.maxPercent;
}

export function isRdShareFractionInRange(share: number, limits: RdShareLimits): boolean {
  if (!Number.isFinite(share)) return false;
  const { minShare, maxShare } = rdShareFractionBounds(limits);
  return share + SHARE_EPS >= minShare && share - SHARE_EPS <= maxShare;
}

/**
 * Default percent for a new assignment: configured max, or min(remaining, max).
 * Never suggests 100% when max is 95.
 */
export function defaultRdPercent(
  remainingPercent: number | null | undefined,
  limits: RdShareLimits,
): number {
  if (remainingPercent == null || !Number.isFinite(remainingPercent)) {
    return limits.maxPercent;
  }
  return Math.min(Math.max(remainingPercent, 0), limits.maxPercent);
}

/**
 * HTML `max` for R&D% number inputs: never above the configured max or the
 * remaining cross-project budget (when known).
 */
export function rdShareInputMaxPercent(
  remainingPercent: number | null | undefined,
  limits: RdShareLimits,
): number {
  return defaultRdPercent(remainingPercent, limits);
}
